import { type ChangeEvent, type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAuth } from "@/core/auth/hooks/useAuth";
import { captureFlowError, captureFlowStep } from "@/core/monitoring/sentry";
import { AuthEntryCard } from "@/features/auth/components/AuthEntryCard";
import { Button } from "@/shared/components/ui/Button";
import { LanguageSelector } from "@/shared/components/ui/LanguageSelector";
import type { LoginFailure } from "@/core/auth/session/session.types";

// ---------------------------------------------------------------------------
// Small inline icons (no external dep needed)
// ---------------------------------------------------------------------------

function EyeIcon({ visible }: { visible: boolean }) {
  return visible ? (
    <svg
      aria-hidden="true"
      fill="none"
      height="20"
      viewBox="0 0 24 24"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  ) : (
    <svg
      aria-hidden="true"
      fill="none"
      height="20"
      viewBox="0 0 24 24"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <line
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        x1="1"
        x2="23"
        y1="1"
        y2="23"
      />
    </svg>
  );
}

function DangerIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="18"
      viewBox="0 0 24 24"
      width="18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <line
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        x1="12"
        x2="12"
        y1="9"
        y2="13"
      />
      <line
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        x1="12"
        x2="12.01"
        y1="17"
        y2="17"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LoginFormState = {
  email: string;
  password: string;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AuthLoginPage() {
  const { t, i18n } = useTranslation(["common", "auth", "errors"]);
  const { login } = useAuth();

  const [form, setForm] = useState<LoginFormState>({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<LoginFailure | null>(null);

  const languageOptions = [
    { label: t("common:language.es"), value: "es" },
    { label: t("common:language.en"), value: "en" },
  ];

  const isLocked =
    loginError?.errorCode === "ACCOUNT_LOCKED" ||
    loginError?.errorCode === "ERR_ACCOUNT_LOCKED";
  const isInactive = loginError?.errorCode === "ACCOUNT_INACTIVE";
  const isRateLimited = loginError?.errorCode === "RATE_LIMITED";
  const hasFieldError =
    loginError?.errorCode === "INVALID_CREDENTIALS" ||
    loginError?.errorCode === "ACCOUNT_LOCKED" ||
    loginError?.errorCode === "ERR_ACCOUNT_LOCKED";

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const emailFormatError =
    form.email.trim().length > 0 && !isEmailValid
      ? t("validation:email.invalid")
      : null;

  const canSubmit =
    isEmailValid &&
    form.password.length > 0 &&
    !isSubmitting &&
    !isLocked &&
    !isInactive &&
    !isRateLimited;

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error on user input.
    if (loginError) setLoginError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setLoginError(null);
    captureFlowStep("login", "submit-attempted");

    try {
      const result = await login(
        form.email.trim(),
        form.password,
        i18n.language,
      );
      if (!result.success) {
        captureFlowStep("login", "login-failed", {
          errorCode: result.errorCode ?? "unknown",
        });
        setLoginError(result);
      } else {
        captureFlowStep("login", "login-success");
      }
    } catch (err) {
      captureFlowError("login", "submit-attempted", err);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleLanguageChange(lang: string) {
    void i18n.changeLanguage(lang);
  }

  const errorMessage = loginError
    ? t(`errors:${loginError.errorCode}`, {
        defaultValue: t("errors:INTERNAL_ERROR"),
      })
    : null;

  const attemptsMessage =
    loginError?.errorCode === "INVALID_CREDENTIALS" &&
    typeof loginError.attemptsRemaining === "number" &&
    loginError.attemptsRemaining > 0
      ? t("auth:login.attemptsRemaining", {
          count: loginError.attemptsRemaining,
        })
      : null;

  return (
    <AuthEntryCard variant="login">
      <div className="flex w-full flex-col items-center">
        <div className="mt-8 w-full max-w-96 text-center">
          <h1 className="m-0 text-xl leading-tight font-bold text-text-primary">
            {t("auth:login.title")}
          </h1>
        </div>

        {errorMessage && (
          <div
            aria-live="polite"
            className="mt-5 flex w-full max-w-96 items-start gap-2 rounded-lg bg-[#fde9eb] px-3 py-2 text-[0.8125rem] leading-[1.45] text-[#2f3640]"
            role="alert"
          >
            <span
              className="mt-[0.05rem] inline-flex shrink-0 items-center text-[#ff3b4f]"
              aria-hidden="true"
            >
              <DangerIcon />
            </span>
            <span className="flex-1">
              {errorMessage}
              {attemptsMessage && (
                <strong className="mt-1 block font-bold text-[#c0392b]">
                  {" "}
                  {attemptsMessage}
                </strong>
              )}
            </span>
          </div>
        )}

        <form
          className="mt-8 flex w-full max-w-96 flex-col gap-3"
          noValidate
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-2.5">
            <label
              className="text-[0.8125rem] leading-6 text-text-muted"
              htmlFor="login-email"
            >
              {t("auth:login.field.email")}
            </label>
            <div className="relative flex items-center">
              <input
                aria-describedby={hasFieldError ? "login-error" : undefined}
                aria-invalid={hasFieldError}
                autoComplete="email"
                className={[
                  "min-h-[2.125rem] w-full rounded border bg-surface-card px-3 py-[0.4375rem] pr-9",
                  "text-[0.8125rem] leading-6 text-text-primary placeholder:text-[#99a7b5]",
                  "transition-[border-color,box-shadow] duration-150 focus-visible:outline-none",
                  hasFieldError
                    ? "border-[#ff3b4f] focus-visible:border-[#ff3b4f] focus-visible:ring-4 focus-visible:ring-[rgba(255,59,79,0.18)]"
                    : "border-border-soft focus-visible:border-brand-primary focus-visible:ring-4 focus-visible:ring-focus-ring",
                ]
                  .filter(Boolean)
                  .join(" ")}
                id="login-email"
                maxLength={254}
                name="email"
                onChange={handleChange}
                placeholder={t("auth:login.field.email.placeholder")}
                required
                type="email"
                value={form.email}
              />
              {hasFieldError && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-4 inline-flex items-center text-[#ff3b4f]"
                >
                  <DangerIcon />
                </span>
              )}
            </div>
            {emailFormatError ? (
              <p
                className="mt-1.5 text-sm leading-[1.4] text-[#e02d3c]"
                role="alert"
              >
                {emailFormatError}
              </p>
            ) : null}
          </div>

          <div className="mt-2 flex flex-col gap-2.5">
            <label
              className="text-[0.8125rem] leading-6 text-text-muted"
              htmlFor="login-password"
            >
              {t("auth:login.field.password")}
            </label>
            <div className="relative flex items-center">
              <input
                aria-describedby={hasFieldError ? "login-error" : undefined}
                aria-invalid={hasFieldError}
                autoComplete="current-password"
                className={[
                  "min-h-[2.125rem] w-full rounded border bg-surface-card px-3 py-[0.4375rem] pr-9",
                  "text-[0.8125rem] leading-6 text-text-primary placeholder:text-[#99a7b5]",
                  "transition-[border-color,box-shadow] duration-150 focus-visible:outline-none",
                  hasFieldError
                    ? "border-[#ff3b4f] focus-visible:border-[#ff3b4f] focus-visible:ring-4 focus-visible:ring-[rgba(255,59,79,0.18)]"
                    : "border-border-soft focus-visible:border-brand-primary focus-visible:ring-4 focus-visible:ring-focus-ring",
                ]
                  .filter(Boolean)
                  .join(" ")}
                id="login-password"
                maxLength={64}
                name="password"
                onChange={handleChange}
                placeholder={t("auth:login.field.password.placeholder")}
                required
                type={showPassword ? "text" : "password"}
                value={form.password}
              />
              <button
                aria-label={
                  showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                }
                className="absolute right-4 inline-flex items-center border-none bg-transparent p-0 text-[#606975] cursor-pointer focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                onClick={() => setShowPassword((v) => !v)}
                type="button"
              >
                <EyeIcon visible={showPassword} />
              </button>
            </div>
          </div>

          <div className="-mt-2 text-right">
            <Link
              className="text-xs font-medium text-brand-primary no-underline hover:underline"
              to="/forgot-password"
            >
              {t("auth:login.link.forgotPassword")}
            </Link>
          </div>

          {isLocked ? (
            <Button
              className="min-h-9 px-5 py-1.5 text-sm"
              fullWidth
              onClick={() => undefined}
              type="button"
              variant="primary"
            >
              <Link className="text-inherit no-underline" to="/forgot-password">
                {t("common:btn.forgotPassword")}
              </Link>
            </Button>
          ) : (
            <Button
              className="min-h-9 px-5 py-1.5 text-sm disabled:bg-[#f1f1f3] disabled:text-[#8c939b] disabled:opacity-100"
              disabled={!canSubmit}
              fullWidth
              type="submit"
              variant="primary"
            >
              {isSubmitting ? "..." : t("common:btn.submit")}
            </Button>
          )}

          <p className="m-0 text-center text-[0.8125rem] text-text-muted">
            {t("auth:login.link.register")}{" "}
            <Link
              className="text-xs font-medium text-brand-primary no-underline hover:underline"
              to="/register/consent"
            >
              {t("auth:login.link.registerAction")}
            </Link>
          </p>
        </form>

        <div className="mt-6">
          <LanguageSelector
            onChange={handleLanguageChange}
            options={languageOptions}
            value={i18n.language}
          />
        </div>
      </div>
    </AuthEntryCard>
  );
}
